import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { setActiveOrgSlug } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import {
  useAcceptInvitation,
  useAcceptInvitationAsNew,
  useInvitationLookup,
} from '@/lib/invitations';

import { AuthCard, FormField } from './LoginPage';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? undefined;
  const me = useMe();
  const lookup = useInvitationLookup(token);

  if (!token) {
    return (
      <AuthCard title="Missing invitation token" subtitle="The link looks incomplete.">
        <p className="text-center text-sm text-stone-600 dark:text-stone-400">
          <Link to="/login" className="text-forge-700 dark:text-forge-400 hover:underline">
            Go to sign-in
          </Link>
        </p>
      </AuthCard>
    );
  }

  if (lookup.isPending) {
    return (
      <AuthCard title="Loading invitation…">
        <p className="text-center text-sm text-stone-500 dark:text-stone-400">Just a moment…</p>
      </AuthCard>
    );
  }
  if (lookup.isError || !lookup.data) {
    return (
      <AuthCard title="Invitation not found" subtitle="This link doesn't match a known invitation.">
        <p className="text-center text-sm text-stone-600 dark:text-stone-400">
          <Link to="/login" className="text-forge-700 dark:text-forge-400 hover:underline">
            Sign in
          </Link>
        </p>
      </AuthCard>
    );
  }

  const inv = lookup.data;
  if (inv.is_revoked) {
    return <Closed title="Invitation revoked" subtitle="The inviter cancelled this invitation." />;
  }
  if (inv.is_used) {
    return (
      <Closed
        title="Already accepted"
        subtitle="This invitation has been used. Sign in to access the org."
      />
    );
  }
  if (inv.is_expired) {
    return (
      <Closed
        title="Invitation expired"
        subtitle="Ask the inviter to send you a new one."
      />
    );
  }

  if (me.data) {
    return <AcceptAsExisting token={token} invitation={inv} />;
  }
  return <AcceptAsNew token={token} invitation={inv} />;
}

function Closed({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <AuthCard title={title} subtitle={subtitle}>
      <p className="text-center text-sm text-stone-600 dark:text-stone-400">
        <Link to="/login" className="text-forge-700 dark:text-forge-400 hover:underline">
          Go to sign-in
        </Link>
      </p>
    </AuthCard>
  );
}

function AcceptAsExisting({
  token,
  invitation,
}: {
  token: string;
  invitation: NonNullable<ReturnType<typeof useInvitationLookup>['data']>;
}) {
  const navigate = useNavigate();
  const accept = useAcceptInvitation();

  return (
    <AuthCard
      title={`Join ${invitation.organization_name}`}
      subtitle={`You'll be added as a ${invitation.role}.`}
    >
      {accept.isError && (
        <p className="mb-3 text-sm text-red-600">{(accept.error as Error).message}</p>
      )}
      <Button
        className="w-full"
        disabled={accept.isPending}
        onClick={async () => {
          try {
            const result = await accept.mutateAsync(token);
            setActiveOrgSlug(result.organization_slug);
            navigate('/');
          } catch {
            /* surfaced above */
          }
        }}
      >
        {accept.isPending ? 'Accepting…' : 'Accept invitation'}
      </Button>
      <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
        Wrong account?{' '}
        <Link to="/login" className="text-forge-700 dark:text-forge-400 hover:underline">
          Sign in as someone else
        </Link>
      </p>
    </AuthCard>
  );
}

function AcceptAsNew({
  token,
  invitation,
}: {
  token: string;
  invitation: NonNullable<ReturnType<typeof useInvitationLookup>['data']>;
}) {
  const navigate = useNavigate();
  const accept = useAcceptInvitationAsNew();

  const [email, setEmail] = useState(invitation.email);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await accept.mutateAsync({
        token,
        email,
        password,
        name: name.trim() || undefined,
      });
      setActiveOrgSlug(invitation.organization_slug);
      navigate('/');
    } catch {
      /* surfaced below */
    }
  };

  return (
    <AuthCard
      title={`Join ${invitation.organization_name}`}
      subtitle={`Create an account to accept this ${invitation.role} invitation.`}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Display name (optional)">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="Email">
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField label="Password">
          <PasswordInput
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormField>

        {accept.isError && (
          <p className="text-sm text-red-600">{(accept.error as Error).message}</p>
        )}

        <Button type="submit" className="w-full" disabled={accept.isPending}>
          {accept.isPending ? 'Creating…' : 'Create account & join'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
        Already have an account?{' '}
        <Link
          to={`/login?next=/accept-invite?token=${token}`}
          className="text-forge-700 dark:text-forge-400 hover:underline"
        >
          Sign in
        </Link>{' '}
        first.
      </p>
    </AuthCard>
  );
}
